import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, ExternalLink, Youtube, Home, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t, i18n } = useTranslation();
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          
          {/* Dekanlık Section */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 bg-gradient-to-br from-hacettepe-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">H</span>
              </div>
              <div>
                <div className="font-bold text-xl text-white">{t('footer.deanOffice')}</div>
                <div className="text-hacettepe-red text-sm font-medium">{t('footer.facultyFullName')}</div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={18} className="text-hacettepe-red mt-1 flex-shrink-0" />
                <div className="text-gray-300 text-sm leading-relaxed">
                  {t('footer.address')}
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 299 20 80</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 297 68 50</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 297 68 51</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={16} className="text-hacettepe-red flex-shrink-0" />
                  <span className="text-gray-300 text-sm">(+90312) 299 20 93</span>
                </div>
              </div>
              
              <a
                href="mailto:fendek@hacettepe.edu.tr"
                className="flex items-center space-x-3 hover:text-hacettepe-red transition-colors group"
              >
                <Mail size={16} className="text-hacettepe-red flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-sm group-hover:text-hacettepe-red">{t('footer.contactUs')}</span>
              </a>
            </div>
          </div>

          {/* Bölümler Section */}
          <div className="space-y-6">
            <h3 className="font-bold text-xl text-white border-b-2 border-hacettepe-red pb-2 inline-block">
              {t('footer.departments')}
            </h3>
            
            <div className="space-y-4">
              <div className="group">
                <a 
                  href="https://aktuerya.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.actuarialScience')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 98</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://biology.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.biology')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 80 00</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://stat.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.statistics')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 00</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://chem.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.chemistry')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 79 40</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
              
              <div className="group">
                <a 
                  href="https://mat.hacettepe.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/70 transition-all duration-300 group-hover:shadow-lg"
                >
                  <div>
                    <div className="font-semibold text-white group-hover:text-hacettepe-red transition-colors">
                      {t('footer.mathematics')}
                    </div>
                    <div className="text-gray-400 text-sm">(+90312) 297 78 50</div>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-hacettepe-red transition-colors" />
                </a>
              </div>
            </div>
          </div>

          {/* Social Media & Quick Links Section */}
          <div className="space-y-6">
            <h3 className="font-bold text-xl text-white border-b-2 border-hacettepe-red pb-2 inline-block">
              {t('footer.followUs')}
            </h3>

            {/* Sosyal Medya İkonları */}
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.instagram.com/hacettepefenfakultesi"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center hover:from-pink-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
                title="Instagram"
              >
                <Instagram size={18} className="text-white" />
              </a>

              <a
                href="https://x.com/hacettepefenfak"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full flex items-center justify-center hover:from-gray-800 hover:to-black transition-all duration-300 transform hover:scale-110 shadow-lg"
                title="X (Twitter)"
              >
                <Twitter size={18} className="text-white" />
              </a>

              <a
                href="https://www.youtube.com/@hacettepeuniversitesifenfa9147"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
                title="YouTube"
              >
                <Youtube size={18} className="text-white" />
              </a>

              <a
                href="https://tr.linkedin.com/in/fen-fak%C3%BCltesi-hacettepe-%C3%BCniversitesi-32775a234"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center hover:from-blue-700 hover:to-blue-900 transition-all duration-300 transform hover:scale-110 shadow-lg"
                title="LinkedIn"
              >
                <Linkedin size={18} className="text-white" />
              </a>
            </div>

            {/* Hızlı Erişim Linkleri */}
            <div className="pt-4 border-t border-gray-700">
              <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">{t('footer.quickLinks') || 'Hızlı Erişim'}</h4>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://fenfakultesi.hacettepe.edu.tr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 transform hover:scale-110"
                  title="Fakülte Anasayfa"
                >
                  <Home size={18} className="text-white" />
                </a>

                <a
                  href="https://www.hacettepe.edu.tr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 transform hover:scale-110"
                  title="Hacettepe Üniversitesi"
                >
                  <Globe size={18} className="text-white" />
                </a>

                <a
                  href="mailto:fendek@hacettepe.edu.tr"
                  className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-all duration-300 transform hover:scale-110"
                  title="E-posta Gönder"
                >
                  <Mail size={18} className="text-white" />
                </a>

                <a
                  href="tel:+903122992080"
                  className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-green-600 transition-all duration-300 transform hover:scale-110"
                  title="Telefon"
                >
                  <Phone size={18} className="text-white" />
                </a>

                <a
                  href="/iletisim"
                  className="w-10 h-10 bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-red-600 transition-all duration-300 transform hover:scale-110"
                  title="İletişim / Harita"
                >
                  <MapPin size={18} className="text-white" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-700 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-300 text-sm">
              © 2024 {t('footer.facultyFullName')}. {t('footer.allRightsReserved')}
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <a href="https://www.hacettepe.edu.tr/gizlilik" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-hacettepe-red transition-colors">
                {t('footer.privacyPolicy')}
              </a>
              <a href="https://www.hacettepe.edu.tr/kullanim-kosullari" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-hacettepe-red transition-colors">
                {t('footer.termsOfUse')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;